import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { showSuccessAlert, showError } from '../../../../core/alert/sweetAlertConfig';

interface CreateRoomModuleProps {
  show: boolean;
  onHide: () => void;
  floorId: number;
  floorName: string;
  onSuccess: () => void;
}

type FormData = {
  name: string;
  description: string;
  floorId: string; // keep as string for the inputs; keep as string in payload too
  status: 'ACTIVE' | 'INACTIVE' | 'ONGOING_REPAIR';
  numberOfDoors: string; // input as string; cast to number on submit
  numberOfWindows: string; // NEW: input as string; cast to number on submit
};

const initialForm = (floorId: number): FormData => ({
  name: '',
  description: '',
  floorId: String(floorId),
  status: 'ACTIVE',
  numberOfDoors: '1',
  numberOfWindows: '1', // NEW default
});

const NAME_MAX = 5;

const CreateRoomModule: React.FC<CreateRoomModuleProps> = ({
  show,
  onHide,
  floorId,
  floorName,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<FormData>(initialForm(floorId));
  const [isLoading, setIsLoading] = useState(false);

  // derived validation
  const nameLen = formData.name.trim().length;
  const nameTooLong = nameLen >= 6; // strictly less than 6

  // Keep floorId synced when modal opens for a different floor
  useEffect(() => {
    if (show) {
      setFormData(prev => ({ ...prev, floorId: String(floorId) }));
    }
  }, [show, floorId]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    // Enforce name < 6 chars (max 5)
    if (name === 'name') {
      setFormData(prev => ({ ...prev, name: value.slice(0, NAME_MAX) }));
      return;
    }

    // Only digits for number fields (allow empty)
    if (name === 'numberOfDoors' || name === 'numberOfWindows') {
      if (value === '' || /^[0-9]+$/.test(value)) {
        setFormData(prev => ({ ...prev, [name]: value }));
      }
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => setFormData(initialForm(floorId));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;

    if (!formData.name.trim()) {
      showError('Please enter a room name');
      return;
    }
    if (formData.name.trim().length >= 6) {
      showError('Name must be less than 6 characters (max 5).');
      return;
    }

    const parsedDoors = formData.numberOfDoors === '' ? NaN : Number(formData.numberOfDoors);
    if (!Number.isInteger(parsedDoors) || parsedDoors < 0) {
      showError('Number of doors must be a non-negative integer');
      return;
    }

    const parsedWindows = formData.numberOfWindows === '' ? NaN : Number(formData.numberOfWindows);
    if (!Number.isInteger(parsedWindows) || parsedWindows < 0) {
      showError('Number of windows must be a non-negative integer');
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description?.trim() || '',
        floorId: formData.floorId, // keep as string to match required payload
        status: formData.status,
        numberOfDoors: parsedDoors,
        numberOfWindows: parsedWindows,
      };

      const response = await apiDefinitions.addRoom(payload);

      if (response.status === 200 || response.status === 201) {
        showSuccessAlert("Room created successfully");
        onSuccess();
        onHide();
        resetForm();
      } else {
        showError(response.data?.statusDescription || 'Failed to create room');
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
      } else if (err.response?.status === 400) {
        showError(err.response?.data?.statusDescription || 'Invalid room data');
      } else if (err.response?.status === 409) {
        showError('Room name already exists on this floor');
      } else {
        showError('Failed to create room. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onHide();
      resetForm();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered backdrop="static" keyboard={!isLoading}>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Add Room to {floorName}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Row className="g-3">
            {/* Left: Editable fields */}
            <Col md={6}>
              <h6 className="mb-2">Create</h6>

              <Form.Group className="mb-3" controlId="roomName">
                <Form.Label>
                  Name <span className="text-muted">(Ex: BMS_L,SED — max 5)</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  placeholder="Enter room name"
                  onChange={handleInputChange}
                  disabled={isLoading}
                  required
                  maxLength={NAME_MAX}
                  pattern=".{0,5}"
                  title="Name must be less than 6 characters (max 5)."
                  isInvalid={nameTooLong}
                />
                <Form.Control.Feedback type="invalid">
                  Name must be less than 6 characters (max 5).
                </Form.Control.Feedback>
                <Form.Text className="text-muted">{nameLen}/5</Form.Text>
              </Form.Group>

              <Form.Group className="mb-3" controlId="roomDescription">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={formData.description}
                  placeholder="Enter description (optional)"
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
              </Form.Group>
            </Col>

            {/* Right: Read-only details & live summary */}
            <Col md={6}>
              <h6 className="mb-2">Details</h6>

              <Form.Group className="mb-3" controlId="roomStatus">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  disabled={isLoading}
                >
                  <option value="ACTIVE">Active</option>
                  <option value="INACTIVE">Inactive</option>
                  <option value="ONGOING_REPAIR">Ongoing Repair</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3" controlId="numberOfDoors">
                <Form.Label>Number of Doors</Form.Label>
                <Form.Control
                  type="number"
                  inputMode="numeric"
                  name="numberOfDoors"
                  value={formData.numberOfDoors}
                  min={0}
                  step={1}
                  placeholder="e.g., 2"
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
              </Form.Group>

              <Form.Group className="mb-0" controlId="numberOfWindows">
                <Form.Label>Number of Windows</Form.Label>
                <Form.Control
                  type="number"
                  inputMode="numeric"
                  name="numberOfWindows"
                  value={formData.numberOfWindows}
                  min={0}
                  step={1}
                  placeholder="e.g., 2"
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
              </Form.Group>

              {/* Keep floorId in form state for submit */}
              <Form.Control type="hidden" name="floorId" value={formData.floorId} />
            </Col>
          </Row>

          <Form.Group className="mt-3 mb-0" controlId="nameRule">
            <Form.Label>Rules</Form.Label>
            <Form.Control
              as="textarea"
              value={`• Name must be < 6 characters\n• Current length: ${nameLen}/5\n• Examples: BMS_L, SED`}
              disabled
              rows={3}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="primary" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="success" type="submit" disabled={isLoading || nameTooLong}>
            {isLoading ? 'Creating...' : 'Create Room'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateRoomModule;
